import { InputType } from 'config'

export const LoanPaymentType = {
  payment: 'Regular Payment',
  otherCashFromBorrower: 'Other Cash From Borrower',
  payoff: 'Payoff',
}

/** Full payment-type labels used by Sample/Import CSV (includes lender cash). */
export const LoanPaymentTypeImportLabels: Record<string, string> = {
  payment: 'Regular Payment',
  otherCashFromBorrower: 'Other Cash From Borrower',
  otherCashFromLender: 'Other Cash From Lender',
  payoff: 'Payoff',
}

export const loanPaymentImportHeaders = [
  'Payment Type',
  'Amount Due',
  'Payment Due Date',
  'Amount Paid',
  'Payment Received',
  'Payment Deposited',
  'Principal',
  'Interest',
  'Mortgage Insurance',
  'To Impound (Excl. MI)',
  'To Impound Total',
  'Buydown Funds',
  'Late Fees',
  'Check / ACH No',
  'Payor / Payee',
  'Notes',
  // Appended, not inserted: import maps columns by position, so a CSV built before this
  // column existed still lines up.
  'Servicing Fee',
]

export const loanPaymentImportKeys = [
  'type',
  'amountDue',
  'paymentDueDate',
  'amountPaid',
  'paymentReceivedDate',
  'paymentDepositedDate',
  'principal',
  'interest',
  'mortgage',
  'escrows',
  'escrowsTotal',
  'buydown',
  'lateFee',
  'checkACH',
  'payor',
  'notes',
  'servicingFee',
] as const

export const loanPaymentTypeLabelToKey: Record<string, string> = Object.fromEntries(
  Object.entries(LoanPaymentTypeImportLabels).map(([key, label]) => [label.toLowerCase(), key]),
)

const withHistory = (input: InputType): InputType => ({ ...input, history: true })

/** Stored payment fields that have audit history (excludes computed/UI-only keys). */
export const loanPaymentAuditFieldKeys = [
  'type',
  'paymentDueDate',
  'paymentReceivedDate',
  'paymentDepositedDate',
  'amountDue',
  'amountPaid',
  'sourceSuspense',
  'sourceImpound',
  'sourceInterestReserve',
  'sourceConstructionReserve',
  'sourceEscrowRepairHoldback',
  'sourceTrustBalance',
  'principal',
  'interest',
  'defaultInterest',
  'unpaidDefaultInterest',
  'unpaidInterest',
  'capitalizedInterest',
  'mortgage',
  'escrows',
  'escrowsTotal',
  'buydown',
  'lateFee',
  'servicingFee',
  'charges',
  'suspense',
  'omitFromReport',
  'checkACH',
  'paymentMethod',
  'payor',
  'notes',
  'files',
  'payCharges',
  'paymentCode',
] as const

export const defaultInputs = (loanNumber: number): Record<string, InputType> => {
  return {
    type: withHistory({
      inputType: 'select',
      options: LoanPaymentType,
      title: 'Payment Type',
      value: 'payment',
      span: 2,
    }),
    custom1: {
      inputType: 'custom',
      title: '',
      span: 'full',
    },
    paymentDueDate: withHistory({
      inputType: 'text',
      type: 'date',
      title: 'Payment Due Date',
      span: 1,
    }),
    paymentReceivedDate: withHistory({
      inputType: 'text',
      type: 'date',
      title: 'Payment Received',
      span: 1,
    }),
    paymentDepositedDate: withHistory({
      inputType: 'text',
      type: 'date',
      title: 'Payment Deposited',
      span: 1,
    }),
    custom2: {
      inputType: 'custom',
      title: 'Source of Funds',
      span: 'full',
    },
    amountDue: withHistory({
      inputType: 'text',
      type: 'thousandSep',
      prefix: '$',
      title: 'Amount Due',
      span: 1,
    }),
    amountPaid: withHistory({
      inputType: 'text',
      type: 'thousandSep',
      prefix: '$',
      title: 'Amount Paid',
      span: 1,
    }),
    sourceSuspense: withHistory({
      inputType: 'text',
      type: 'thousandSep',
      prefix: '$',
      title: 'From Suspense',
      span: 1,
    }),
    sourceImpound: withHistory({
      inputType: 'text',
      type: 'thousandSep',
      prefix: '$',
      title: 'From Impound',
      span: 1,
    }),
    // The interest reserve paying the period's interest instead of the borrower. Draws the Interest
    // Reserve ledger when the payment posts; reversing or deleting the payment puts it back.
    sourceInterestReserve: withHistory({
      inputType: 'text',
      type: 'thousandSep',
      prefix: '$',
      title: 'From Interest Reserve',
      span: 1,
    }),
    // Reserve money the servicer is already holding in the loan's trust ledger paying the loan
    // instead of the borrower, which is how a payoff on a converted construction loan settles.
    // Draws that ledger when the payment posts; reversing or deleting the payment puts it back.
    sourceConstructionReserve: withHistory({
      inputType: 'text',
      type: 'thousandSep',
      prefix: '$',
      title: 'From Construction Reserve',
      span: 1,
    }),
    // Borrower money the servicer holds in a trust sub-ledger bucket (an escrow repair holdback)
    // paying the loan instead of the borrower. Its own field rather than the Construction Reserve
    // one above because the two buckets are stored in different places and the draw has to come
    // out of the one holding the money: on a converted loan the trust-ledger table is empty and
    // the holdback is the whole balance. Same name as the payoff credit and the Loan Balances tile.
    // The escrow repair holdback bucket paying the loan instead of the borrower. Draws the
    // sub-ledger bucket, which on a converted loan is where the money actually is.
    sourceEscrowRepairHoldback: withHistory({
      inputType: 'text',
      type: 'thousandSep',
      prefix: '$',
      title: 'From Escrow Holdback',
      span: 1,
    }),
    // The loan's OWN trust ledger. A different table from the bucket above, and the draw has to
    // come out of the one holding the money - which is why these are two boxes and not one.
    sourceTrustBalance: withHistory({
      inputType: 'text',
      type: 'thousandSep',
      prefix: '$',
      title: 'From Trust Balance',
      span: 1,
    }),
    custom3: {
      inputType: 'custom',
      title: 'Applies To',
      span: 'full',
    },
    principal: withHistory({
      inputType: 'text',
      type: 'thousandSep',
      prefix: '$',
      title: 'Principal',
      span: 1,
    }),
    interest: withHistory({
      inputType: 'text',
      type: 'thousandSep',
      prefix: '$',
      title: 'Interest',
      span: 1,
    }),
    defaultInterest: withHistory({
      inputType: 'text',
      type: 'thousandSep',
      prefix: '$',
      title: 'Default Interest',
      span: 1,
    }),
    // Default interest this payment assessed and did not collect. Read-only, like Unpaid Interest
    // beside it: the figure is the gap between what the default rate assessed and what the servicer
    // chose (or the funds allowed) to collect in Default Interest above, so it is derived from that
    // field rather than typed.
    unpaidDefaultInterest: withHistory({
      inputType: 'text',
      type: 'thousandSep',
      prefix: '$',
      title: 'Unpaid Default Interest',
      disabled: true,
      span: 1,
      tooltip:
        'Default-rate interest assessed on this payment that the payment did not collect. It is ' +
        'booked as an Unpaid Default Interest charge on the loan - its own balance, separate from ' +
        'Unpaid Interest - and collected on a payoff or reinstatement quote.',
    }),
    unpaidInterest: withHistory({
      inputType: 'text',
      type: 'thousandSep',
      prefix: '$',
      title: 'Unpaid Interest',
      disabled: true,
      span: 1,
    }),
    // Shown only on a loan set to "Principal (Negative Amortization)" - see the modal. The two are
    // alternatives: a shortfall is either tracked as a receivable above or capitalized here.
    capitalizedInterest: withHistory({
      inputType: 'text',
      type: 'thousandSep',
      prefix: '$',
      title: 'Capitalized Interest',
      disabled: true,
      span: 1,
      tooltip:
        'Interest this payment did not cover, added to the principal balance because the loan is ' +
        'set to Principal (Negative Amortization). The balance grows by this amount and interest ' +
        'accrues on it from the next period.',
    }),
    mortgage: withHistory({
      inputType: 'text',
      type: 'thousandSep',
      prefix: '$',
      title: 'Mortgage Insurance',
      span: 1,
    }),
    escrows: withHistory({
      inputType: 'text',
      type: 'thousandSep',
      prefix: '$',
      title: 'To Impound (Excl. MI)',
      span: 1,
    }),
    escrowsTotal: withHistory({
      inputType: 'text',
      type: 'thousandSep',
      prefix: '$',
      title: 'To Impound Total',
      span: 1,
    }),
    buydown: withHistory({
      inputType: 'text',
      type: 'thousandSep',
      prefix: '$',
      title: 'Buydown Funds',
      span: 1,
    }),
    lateFee: withHistory({
      inputType: 'text',
      type: 'thousandSep',
      prefix: '$',
      title: 'Late Fees',
      span: 1,
    }),
    servicingFee: withHistory({
      inputType: 'text',
      type: 'thousandSep',
      prefix: '$',
      title: 'Servicing Fee',
      span: 1,
    }),
    charges: withHistory({
      inputType: 'text',
      type: 'thousandSep',
      prefix: '$',
      title: 'Charges',
      disabled: true,
      span: 1,
    }),
    suspense: withHistory({
      inputType: 'text',
      type: 'thousandSep',
      prefix: '$',
      title: 'Suspense',
      span: 1,
    }),
    totalCredited: {
      inputType: 'text',
      type: 'thousandSep',
      prefix: '$',
      title: 'Total Credited',
      disabled: true,
      span: 1,
    },
    custom4: {
      inputType: 'custom',
      title: '',
      span: 'full',
    },
    btnPayCharges: {
      inputType: 'custom',
      title: '',
      span: 'full',
    },
    omitFromReport: withHistory({
      inputType: 'checkbox',
      title: 'Omit From 1098 Reporting',
      value: false,
      span: 1,
    }),
    payCharges: {
      inputType: 'custom',
      title: '',
      span: 1,
    },
    custom5: {
      inputType: 'custom',
      title: '',
      span: 'full',
    },
    checkACH: withHistory({
      inputType: 'text',
      title: 'Check / ACH No',
      span: 1,
    }),
    // How the money arrived. Options come from the company's PAYMENT_METHOD list and are filled
    // in by the modal; blank stays allowed so imported history isn't forced to invent a method.
    paymentMethod: withHistory({
      inputType: 'select',
      options: [],
      hasDefaultOption: true,
      title: 'Payment Method',
      span: 1,
    }),
    payor: withHistory({
      inputType: 'text',
      title: 'Payor / Payee',
      span: 1,
    }),
    notes: withHistory({
      inputType: 'textarea',
      title: 'Notes',
      rows: 3,
      span: 'full',
    }),
    files: withHistory({
      inputType: 'FileTable',
      title: 'Files',
      required: false,
      showCategory: false,
      filePath: `loan/${loanNumber}/loanPayment`,
      span: 'full',
      showStatus: false,
      multiple: true,
      additionalActions: [],
    }),
  }
}
